import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getSocieteInfos, COLORS, getDocumentStyles } from "./exportUtils";
import { formatCurrencyForExport, formatNumberForExport, formatDateForExport } from "./exportFormatters";

/**
 * Génère un PDF élégant pour le rapport CA
 */
export const generateCAPDF = (data, selectedYear, selectedMonth) => {
  return new Promise((resolve, reject) => {
    try {
      const societe = getSocieteInfos();
      const styles = getDocumentStyles();
      
      const mois = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      
      // Création du document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Police par défaut
      doc.setFont('helvetica');
      
      // === EN-TÊTE ===
      // Logo (texte stylisé)
      doc.setFontSize(24);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text(societe.nom, 105, 20, { align: 'center' });
      
      // Titre du document
      doc.setFontSize(16);
      doc.setTextColor(COLORS.secondary);
      doc.text(`RAPPORT D'ACTIVITÉ ${selectedYear}`, 105, 30, { align: 'center' });
      
      // Informations société
      doc.setFontSize(9);
      doc.setTextColor(COLORS.dark);
      doc.setFont(undefined, 'normal');
      doc.text(societe.adresse, 105, 38, { align: 'center' });
      doc.text(`Tél: ${societe.telephone} | Email: ${societe.email} | RC: ${societe.rc}`, 105, 44, { align: 'center' });
      
      // Ligne de séparation
      doc.setDrawColor(COLORS.light);
      doc.setLineWidth(0.5);
      doc.line(20, 50, 190, 50);
      
      // === PÉRIODE ===
      doc.setFontSize(11);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text(`Période: ${mois[selectedMonth-1]} ${selectedYear}`, 20, 60);
      
      // === RÉSUMÉ RAPIDE ===
      const caMensuel = data?.annuel?.find(m => m.mois === selectedMonth)?.total_ttc || 0;
      const caAnnuel = data?.annuel?.reduce((acc, m) => acc + (m.total_ttc || 0), 0) || 0;
      const nbFacturesMois = data?.annuel?.find(m => m.mois === selectedMonth)?.nb_factures || 0;
      
      doc.setFontSize(10);
      doc.setTextColor(COLORS.dark);
      doc.setFont(undefined, 'normal');
      doc.text(`CA Mensuel: ${formatCurrencyForExport(caMensuel)} FCFA`, 20, 70);
      doc.text(`CA Annuel: ${formatCurrencyForExport(caAnnuel)} FCFA`, 20, 77);
      doc.text(`Nombre de factures: ${formatNumberForExport(nbFacturesMois)}`, 20, 84);
      
      // === TABLEAU JOURNALIER ===
      doc.setFontSize(12);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text('Détail journalier', 20, 100);
      
      // Préparer les données du tableau
      const journaux = data?.mensuel_detail || [];
      const journauxData = journaux.map(jour => [
        jour.jour,
        formatCurrencyForExport(jour.ca_journalier),
        formatNumberForExport(jour.nb_factures || 0),
        formatCurrencyForExport(jour.panier_moyen || 0),
        formatCurrencyForExport(jour.tva || 0)
      ]);
      
      // Ajouter une ligne de total
      const totalJournalier = journaux.reduce((acc, j) => acc + j.ca_journalier, 0);
      const totalFacturesJour = journaux.reduce((acc, j) => acc + (j.nb_factures || 0), 0);
      const totalTVAJour = journaux.reduce((acc, j) => acc + (j.tva || 0), 0);
      
      // Tableau principal
      autoTable(doc, {
        startY: 105,
        head: [['Jour', 'CA Journalier', 'Nb Factures', 'Panier Moyen', 'TVA']],
        body: journauxData,
        foot: [['TOTAL', 
                formatCurrencyForExport(totalJournalier) + ' FCFA', 
                formatNumberForExport(totalFacturesJour), 
                '', 
                formatCurrencyForExport(totalTVAJour) + ' FCFA']],
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          lineColor: COLORS.light,
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: COLORS.light,
          textColor: COLORS.primary,
          fontStyle: 'bold',
          halign: 'center'
        },
        footStyles: {
          fillColor: COLORS.white,
          textColor: COLORS.primary,
          fontStyle: 'bold',
          lineWidth: 1,
          lineColor: COLORS.primary
        },
        columnStyles: {
          0: { halign: 'center' },
          1: { halign: 'right' },
          2: { halign: 'center' },
          3: { halign: 'right' },
          4: { halign: 'right' }
        },
        margin: { left: 20, right: 20 }
      });
      
      // === TABLEAU ANNUEL ===
      const lastY = doc.lastAutoTable.finalY + 15;
      
      // Vérifier si on doit ajouter une nouvelle page
      let newY;
      if (lastY > 250) {
        doc.addPage();
        newY = 20;
      } else {
        newY = lastY;
      }
      
      doc.setFontSize(12);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text('Récapitulatif mensuel', 20, newY);
      
      // Données annuelles
      const annuelData = data?.annuel?.map(m => [
        mois[m.mois-1],
        formatNumberForExport(m.nb_factures || 0),
        formatNumberForExport(m.nb_clients || 0),
        formatCurrencyForExport(m.total_ht || 0) + ' FCFA',
        formatCurrencyForExport(m.total_tva || 0) + ' FCFA',
        formatCurrencyForExport(m.total_ttc || 0) + ' FCFA',
        formatCurrencyForExport(m.total_regle || 0) + ' FCFA'
      ]) || [];
      
      autoTable(doc, {
        startY: newY + 5,
        head: [['Mois', 'Fact.', 'Clients', 'HT', 'TVA', 'TTC', 'Réglé']],
        body: annuelData,
        foot: [[
          'TOTAL',
          formatNumberForExport(data?.annuel?.reduce((acc, m) => acc + (m.nb_factures || 0), 0) || 0),
          '',
          formatCurrencyForExport(data?.annuel?.reduce((acc, m) => acc + (m.total_ht || 0), 0) || 0) + ' FCFA',
          formatCurrencyForExport(data?.annuel?.reduce((acc, m) => acc + (m.total_tva || 0), 0) || 0) + ' FCFA',
          formatCurrencyForExport(caAnnuel) + ' FCFA',
          formatCurrencyForExport(data?.annuel?.reduce((acc, m) => acc + (m.total_regle || 0), 0) || 0) + ' FCFA'
        ]],
        theme: 'plain',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          lineColor: COLORS.light,
        },
        headStyles: {
          fillColor: COLORS.light,
          textColor: COLORS.primary,
          fontStyle: 'bold',
          halign: 'center'
        },
        footStyles: {
          fillColor: COLORS.white,
          textColor: COLORS.primary,
          fontStyle: 'bold',
          lineWidth: 1,
          lineColor: COLORS.primary
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'center' },
          2: { halign: 'center' },
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right', fontStyle: 'bold' },
          6: { halign: 'right' }
        },
        margin: { left: 20, right: 20 }
      });
      
      // === TOP CLIENTS ===
      const clientsY = doc.lastAutoTable.finalY + 15;
      
      let clientsStartY;
      if (clientsY > 270) {
        doc.addPage();
        clientsStartY = 20;
      } else {
        clientsStartY = clientsY;
      }
      
      doc.setFontSize(12);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text('Top 5 Clients', 20, clientsStartY);
      
      const clientsData = data?.top_clients?.map(client => [
        client.raison_sociale,
        client.code_client,
        formatNumberForExport(client.nb_factures),
        formatCurrencyForExport(client.total_ttc) + ' FCFA'
      ]) || [];
      
      autoTable(doc, {
        startY: clientsStartY + 5,
        head: [['Client', 'Code', 'Factures', 'CA TTC']],
        body: clientsData,
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: COLORS.light,
          textColor: COLORS.primary,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'center' },
          2: { halign: 'center' },
          3: { halign: 'right', fontStyle: 'bold' }
        },
        margin: { left: 20, right: 20 }
      });
      
      // === TOP PRODUITS ===
      if (data?.top_produits && data.top_produits.length > 0) {
        const produitsY = doc.lastAutoTable.finalY + 15;
        
        let produitsStartY;
        if (produitsY > 270) {
          doc.addPage();
          produitsStartY = 20;
        } else {
          produitsStartY = produitsY;
        }
        
        doc.setFontSize(12);
        doc.setTextColor(COLORS.primary);
        doc.setFont(undefined, 'bold');
        doc.text('Top Produits', 20, produitsStartY);
        
        const produitsData = data.top_produits.slice(0, 5).map(produit => [
          produit.designation,
          produit.reference,
          formatNumberForExport(produit.quantite_vendue || 0),
          formatCurrencyForExport(produit.montant_total || 0) + ' FCFA'
        ]);
        
        autoTable(doc, {
          startY: produitsStartY + 5,
          head: [['Produit', 'Réf.', 'Qté', 'Montant']],
          body: produitsData,
          theme: 'plain',
          styles: {
            fontSize: 9,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: COLORS.light,
            textColor: COLORS.primary,
            fontStyle: 'bold'
          },
          columnStyles: {
            0: { halign: 'left' },
            1: { halign: 'center' },
            2: { halign: 'center' },
            3: { halign: 'right', fontStyle: 'bold' }
          },
          margin: { left: 20, right: 20 }
        });
      }
      
      // === PIED DE PAGE ===
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(COLORS.secondary);
        doc.setFont(undefined, 'italic');
        
        // Date de génération
        const dateGen = new Date().toLocaleDateString('fr-FR');
        doc.text(`Document généré le ${dateGen}`, 20, 285);
        
        // Numéro de page
        doc.text(`Page ${i} / ${totalPages}`, 180, 285, { align: 'right' });
        
        // Mention de confidentialité
        doc.setFontSize(7);
        doc.text(`Document confidentiel - ${societe.nom}`, 105, 292, { align: 'center' });
      }
      
      // Sauvegarder le PDF
      const fileName = `CA_${selectedYear}_${selectedMonth}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      resolve(true);
    } catch (error) {
      console.error("Erreur génération PDF:", error);
      reject(error);
    }
  });
};

/**
 * Exporte les données au format PDF simplifié (sans détails)
 */
export const generateSimplePDF = (data, selectedYear) => {
  return new Promise((resolve, reject) => {
    try {
      const societe = getSocieteInfos();
      const doc = new jsPDF();
      
      // En-tête simplifié
      doc.setFontSize(20);
      doc.setTextColor(COLORS.primary);
      doc.text(`Rapport Annuel ${selectedYear}`, 105, 20, { align: 'center' });
      
      doc.setFontSize(10);
      doc.text(societe.nom, 105, 30, { align: 'center' });
      doc.text(`Édité le ${new Date().toLocaleDateString('fr-FR')}`, 105, 35, { align: 'center' });
      
      // Données annuelles
      const annuelData = data?.annuel?.map(m => {
        const moisNoms = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        return [
          moisNoms[m.mois-1],
          formatCurrencyForExport(m.total_ttc || 0) + ' FCFA'
        ];
      }) || [];
      
      autoTable(doc, {
        startY: 45,
        head: [['Mois', 'CA TTC']],
        body: annuelData,
        theme: 'striped',
        styles: { fontSize: 10 }
      });
      
      const fileName = `CA_annuel_${selectedYear}_simple.pdf`;
      doc.save(fileName);
      
      resolve(true);
    } catch (error) {
      console.error("Erreur PDF simple:", error);
      reject(error);
    }
  });
};